import React from 'react';
import { Card, CardContent } from './ui/CCard';
import { Info } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { Tooltip } from './ui/Tooltip';

export default function VerdictMatrix({ institutionalBenchmark, scoringBreakdown, orgSettings }) {
  const { theme } = useTheme();

  // 1. Clinical Safety
  const safetyScore = scoringBreakdown?.safety_score ?? 0;
  const isSafetyPass = safetyScore >= 80;

  // 2. Competency Band Calculation
  const compScore = scoringBreakdown?.uncapped_competency_pct ?? scoringBreakdown?.competency_score ?? 0;

  // Org thresholds
  const expert = orgSettings?.expectation_expert ?? 75;
  const intermediate = orgSettings?.expectation_intermediate ?? 65;
  const novice = orgSettings?.expectation_novice ?? 55;

  const getCompetencyLevel = (score) => {
    if (score >= expert + 10) return 'Expert';
    if (score >= expert) return 'Proficient';
    if (score >= intermediate) return 'Competent';
    if (score >= novice) return 'Advanced Beginner';
    return 'Novice';
  };

  const currentLevel = getCompetencyLevel(compScore);

  const getStatusColor = (isPass) => {
    return isPass ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400';
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 'Novice': return 'text-red-600 dark:text-red-400';
      case 'Advanced Beginner': return 'text-orange-600 dark:text-orange-400';
      case 'Competent': return 'text-yellow-600 dark:text-yellow-400';
      case 'Proficient': return 'text-blue-600 dark:text-blue-400';
      case 'Expert': return 'text-green-600 dark:text-green-400';
      default: return 'text-secondary-500 dark:text-secondary-500';
    }
  };

  return (
    <div className="space-y-6">
      <div className={`p-4 rounded-xl flex items-center space-x-3 ${theme === 'dark' ? 'bg-blue-900/20 text-blue-300 border border-blue-800' : 'bg-blue-50 text-blue-700 border border-blue-200'}`}>
        <Info className="w-5 h-5 flex-shrink-0" />
        <p className="text-sm font-medium">
          These Departmental Benchmarks are calculated based on your organization's performance thresholds.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Clinical Safety */}
        <Card className="p-6 bg-white dark:bg-secondary-800 shadow-lg rounded-2xl border border-secondary-200 dark:border-secondary-700">
          <CardContent className="p-0">
            <h3 className="text-lg font-semibold text-secondary-800 dark:text-secondary-100 mb-2">Clinical Safety</h3>
            <p className={`text-3xl font-bold ${getStatusColor(isSafetyPass)} mb-1`}>
              {safetyScore}%
            </p>
            <p className={`text-sm font-black ${getStatusColor(isSafetyPass)} uppercase tracking-widest`}>
              {isSafetyPass ? ">= 80% PASS" : "< 80% FAIL"}
            </p>
          </CardContent>
        </Card>

        {/* Competency Band */}
        <Card className="p-6 bg-white dark:bg-secondary-800 shadow-lg rounded-2xl border border-secondary-200 dark:border-secondary-700">
          <CardContent className="p-0">
            <div className="flex items-center space-x-2 mb-2">
              <h3 className="text-lg font-semibold text-secondary-800 dark:text-secondary-100">Competency Band</h3>
              <Tooltip 
                content={
                  <div className="space-y-1 text-left whitespace-nowrap p-1">
                    <p className="font-semibold border-b border-gray-600 pb-1 mb-1 text-gray-200">Department Benchmarks</p>
                    <p>≥ {expert + 10}% : <span className="text-green-400 font-medium">Expert</span></p>
                    <p>≥ {expert}% : <span className="text-blue-400 font-medium">Proficient</span></p>
                    <p>≥ {intermediate}% : <span className="text-yellow-400 font-medium">Competent</span></p>
                    <p>≥ {novice}% : <span className="text-orange-400 font-medium">Adv. Beginner</span></p>
                    <p>&lt; {novice}% : <span className="text-red-400 font-medium">Novice</span></p>
                  </div>
                }
                position="bottom"
              >
                <div className="text-secondary-400 hover:text-secondary-600 dark:hover:text-secondary-200 cursor-pointer">
                  <Info className="w-4 h-4" />
                </div>
              </Tooltip>
            </div>
            <p className={`text-3xl font-bold ${getLevelColor(currentLevel)} mb-3`}>
              {currentLevel}
            </p>
            <p className="text-sm text-secondary-600 dark:text-secondary-400">
              Overall Score: <span className="font-bold text-primary-600">{compScore}%</span>
            </p>
          </CardContent>
        </Card>

        {/* Growth Focus */}
        <Card className="p-6 bg-white dark:bg-secondary-800 shadow-lg rounded-2xl border border-secondary-200 dark:border-secondary-700">
          <CardContent className="p-0">
            <h3 className="text-lg font-semibold text-secondary-800 dark:text-secondary-100 mb-2">Growth Focus</h3>
            <p className="text-3xl font-bold text-primary-600 dark:text-primary-400 mb-3">
              {scoringBreakdown?.optimization_band?.focus_area || 'Clinical Depth'}
            </p>
            <p className="text-sm text-secondary-600 dark:text-secondary-400">
              Personalized transition strategy identified.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

